import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Notifications } from "@mantine/notifications";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import Unauthorized from "./pages/Unauthorized";
import Dashboard from "./pages/Dashboard";
import Overview from "./pages/Dashboard/Overview";
import Analytics from "./pages/Dashboard/Analytics";
import Users from "./pages/Dashboard/Users";
import UserDetails from "./pages/Dashboard/Users/Details";
import Devices from "./pages/Dashboard/Devices";
import AllowedUsers from "./pages/Dashboard/AllowedUsers";
import Deletions from "./pages/Dashboard/Users/Deletions";
import Archive from "./pages/Dashboard/Users/Archive";
import Leaderboard from "./pages/Dashboard/Leaderboard";
import Waitlist from "./pages/Dashboard/Waitlist";

import MissionsPage from "./pages/Dashboard/Missions";
import MissionCreate from "./pages/Dashboard/Missions/New/index";
import MissionUpdate from "./pages/Dashboard/Missions/Update/index";
import MissionDetails from "./pages/Dashboard/Missions/Details/index";
import CampaignList from "./pages/Dashboard/Campaigns/index";
import CampaignCreate from "./pages/Dashboard/Campaigns/New/index";
import CampaignUpdate from "./pages/Dashboard/Campaigns/Update/index";
import CampaignDetails from "./pages/Dashboard/Campaigns/Details/index";
import Withdrawals from "./pages/Dashboard/Withdrawals";
import Proofs from "./pages/Dashboard/Proofs";
import FraudReview from "./pages/Dashboard/FraudReview";
import Finance from "./pages/Dashboard/Finance";
import Funnel from "./pages/Dashboard/Funnel";
import AccessControl from "./pages/Dashboard/AccessControl";
import MembersGroup from "./pages/Dashboard/groups/Members";
import AccessGroup from "./pages/Dashboard/groups/Access";
import ContentGroup from "./pages/Dashboard/groups/Content";
import ReviewGroup from "./pages/Dashboard/groups/Review";
import MoneyGroup from "./pages/Dashboard/groups/Money";
import GrowthGroup from "./pages/Dashboard/groups/Growth";
import SystemGroup from "./pages/Dashboard/groups/System";
import Team from "./pages/Dashboard/Team";
import AuditLogs from "./pages/Dashboard/Audit";
import AnnouncementsPage from "./pages/Dashboard/Announcements";
import SocialLinks from "./pages/Dashboard/Social";
import Verification from "./pages/Dashboard/Verification";
import ControlCenter from "./pages/Dashboard/ControlCenter";
import LevelsPage from "./pages/Dashboard/Level";
import FeedbackPage from "./pages/Dashboard/Feedback";
import SquadsPage from "./pages/Dashboard/Squads";
import SquadDetails from "./pages/Dashboard/Squads/Details";
import SettingsPage from "./pages/Dashboard/Settings";
import NotFound404 from "./pages/NotFound404";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthRedirect from "./components/AuthRedirect";
import RequirePermission from "./components/RequirePermission";
import { AdminProvider } from "./context/AdminContext";
import { AdminPermissions } from "./lib/data";

function App() {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <AuthRedirect />,
    },
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/reset-password",
      element: <ResetPassword />,
    },
    {
      path: "/onboarding/:token",
      element: <Onboarding />,
    },
    {
      path: "/unauthorized",
      element: <Unauthorized />,
    },
    {
      path: "/d",
      element: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Overview />,
        },
        {
          path: "analytics",
          element: <Analytics />,
        },
        {
          path: "analytics/:section",
          element: <Analytics />,
        },
        {
          path: "users",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Users />
            </RequirePermission>
          ),
        },
        {
          path: "verification",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_VERIFICATION}
            >
              <Verification />
            </RequirePermission>
          ),
        },
        {
          // Access is enforced inside the page (Anointed + Favour email allow-list)
          // and by the email-gated nav item, matching the Verification tab.
          path: "control-center",
          element: <ControlCenter />,
        },
        {
          path: "users/:userId",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <UserDetails />
            </RequirePermission>
          ),
        },
        {
          path: "devices",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Devices />
            </RequirePermission>
          ),
        },
        {
          path: "allowed-users",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <AllowedUsers />
            </RequirePermission>
          ),
        },
        {
          path: "deletions",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Deletions />
            </RequirePermission>
          ),
        },
        {
          path: "archive",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Archive />
            </RequirePermission>
          ),
        },
        {
          path: "leaderboard",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Leaderboard />
            </RequirePermission>
          ),
        },
        {
          path: "waitlist",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Waitlist />
            </RequirePermission>
          ),
        },
        {
          path: "missions",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <MissionsPage />
            </RequirePermission>
          ),
        },
        {
          path: "campaigns",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <CampaignList />
            </RequirePermission>
          ),
        },
        {
          path: "campaigns/new",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <CampaignCreate />
            </RequirePermission>
          ),
        },
        {
          path: "campaigns/update/:campaignId",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <CampaignUpdate />
            </RequirePermission>
          ),
        },
        {
          path: "campaigns/:campaignId",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <CampaignDetails />
            </RequirePermission>
          ),
        },
        {
          path: "missions/new",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <MissionCreate />
            </RequirePermission>
          ),
        },
        {
          path: "missions/update/:taskId",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <MissionUpdate />
            </RequirePermission>
          ),
        },
        {
          path: "missions/:missionId",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <MissionDetails />
            </RequirePermission>
          ),
        },

        {
          path: "withdrawals",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_WITHDRAWALS}
            >
              <Withdrawals />
            </RequirePermission>
          ),
        },
        {
          path: "proofs",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <Proofs />
            </RequirePermission>
          ),
        },
        {
          path: "fraud-review",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_PROOFS}
            >
              <FraudReview />
            </RequirePermission>
          ),
        },
        {
          path: "finance",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_WITHDRAWALS}
            >
              <Finance />
            </RequirePermission>
          ),
        },
        {
          path: "funnel",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <Funnel />
            </RequirePermission>
          ),
        },
        {
          // Email-gated inside the component + server allow-list; no permission
          // wrapper (it's limited to specific people, not a role).
          path: "verification",
          element: <AccessControl />,
        },
        // Grouped sidebar sections (each renders existing pages as inner tabs;
        // TabbedGroup filters inner tabs by permission). Old routes above still
        // resolve for deep-links.
        { path: "members", element: <MembersGroup /> },
        { path: "access", element: <AccessGroup /> },
        { path: "content", element: <ContentGroup /> },
        { path: "review", element: <ReviewGroup /> },
        { path: "money", element: <MoneyGroup /> },
        { path: "growth", element: <GrowthGroup /> },
        { path: "system", element: <SystemGroup /> },
        {
          path: "team",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_ADMINS}
            >
              <Team />
            </RequirePermission>
          ),
        },
        {
          path: "audit",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.VIEW_AUDIT_LOGS}
            >
              <AuditLogs />
            </RequirePermission>
          ),
        },
        {
          path: "announcements",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_MISSIONS}
            >
              <AnnouncementsPage />
            </RequirePermission>
          ),
        },
        {
          path: "social",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <SocialLinks />
            </RequirePermission>
          ),
        },

        {
          path: "levels",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_LEVELS}
            >
              <LevelsPage />
            </RequirePermission>
          ),
        },
        {
          path: "feedback",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <FeedbackPage />
            </RequirePermission>
          ),
        },
        {
          path: "squads",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <SquadsPage />
            </RequirePermission>
          ),
        },
        {
          path: "squads/:id",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_USERS}
            >
              <SquadDetails />
            </RequirePermission>
          ),
        },
        {
          path: "settings",
          element: (
            <RequirePermission
              requiredPermission={AdminPermissions.MANAGE_CONFIG}
            >
              <SettingsPage />
            </RequirePermission>
          ),
        },
      ],
    },
    {
      path: "*",
      element: <NotFound404 />,
    },
  ]);

  return (
    <AdminProvider>
      <Notifications />
      <RouterProvider router={router} />
    </AdminProvider>
  );
}

export default App;
